import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { assistantRealtimeReducer, createFeatureBundleStore } from '@triedge/rtk-abstractions';
import { admissionsFeatureBundle } from '../admissions/featureBundle';
import { settingsFeatureBundle } from '../settings/featureBundle';
import { domainRouterApi } from './domainRouterApi';
import { domainRouterUniversalApi } from './domainRouterUniversalApi';
import { admissionsLiveReducer } from './live-data/admissionsLiveSlice';
import { facultyLiveReducer } from './live-data/facultyLiveSlice';
import { financialAidLiveReducer } from './live-data/financialAidLiveSlice';
import { parentPortalLiveReducer } from './live-data/parentPortalLiveSlice';
import { reportGenerationFilterSlice } from './reportGenerationFilterSlice';
import { reportGenerationWorkspaceSlice } from '../report-generation/reportGenerationWorkspaceSlice';
import {
  ystnjRtkFetchSideLaneMiddleware,
  ystnjRtkFetchSideLaneReducer,
} from './rtkFetchSideLane/ystnjRtkFetchSideLane';
import { settingsLiveReducer } from './live-data/settingsLiveSlice';
import { studentsFamiliesLiveReducer } from './live-data/studentsFamiliesLiveSlice';
import { shellSlice } from './shellSlice';

export const store = createFeatureBundleStore({
  bundles: [admissionsFeatureBundle, settingsFeatureBundle],
  extraReducers: {
    shell: shellSlice.reducer,
    admissionsLive: admissionsLiveReducer,
    financialAidLive: financialAidLiveReducer,
    settingsLive: settingsLiveReducer,
    parentPortalLive: parentPortalLiveReducer,
    studentsFamiliesLive: studentsFamiliesLiveReducer,
    facultyLive: facultyLiveReducer,
    reportGenerationFilters: reportGenerationFilterSlice.reducer,
    reportGenerationWorkspace: reportGenerationWorkspaceSlice.reducer,
    assistantRealtime: assistantRealtimeReducer,
    rtkFetchSideLane: ystnjRtkFetchSideLaneReducer,
    [domainRouterApi.reducerPath]: domainRouterApi.reducer,
    [domainRouterUniversalApi.reducerPath]: domainRouterUniversalApi.reducer,
  },
  extraMiddleware: [
    domainRouterApi.middleware,
    domainRouterUniversalApi.middleware,
    ystnjRtkFetchSideLaneMiddleware,
  ],
});

export type RootState = ReturnType<typeof store.getState> & {
  shell: ReturnType<typeof shellSlice.reducer>;
  admissionsLive: ReturnType<typeof admissionsLiveReducer>;
  financialAidLive: ReturnType<typeof financialAidLiveReducer>;
  settingsLive: ReturnType<typeof settingsLiveReducer>;
  parentPortalLive: ReturnType<typeof parentPortalLiveReducer>;
  studentsFamiliesLive: ReturnType<typeof studentsFamiliesLiveReducer>;
  facultyLive: ReturnType<typeof facultyLiveReducer>;
  reportGenerationFilters: ReturnType<typeof reportGenerationFilterSlice.reducer>;
  reportGenerationWorkspace: ReturnType<typeof reportGenerationWorkspaceSlice.reducer>;
  assistantRealtime: ReturnType<typeof assistantRealtimeReducer>;
  rtkFetchSideLane: ReturnType<typeof ystnjRtkFetchSideLaneReducer>;
  [domainRouterApi.reducerPath]: ReturnType<typeof domainRouterApi.reducer>;
  [domainRouterUniversalApi.reducerPath]: ReturnType<typeof domainRouterUniversalApi.reducer>;
};

export type AppDispatch = typeof store.dispatch & ThunkDispatch<RootState, unknown, AnyAction>;
